import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { apiFetch } from '../api/client'
import type { Glow } from '../api/types'

// BulkMoveResult is the outcome of a bulk category move. The mutation never
// throws on a per-glow failure — it resolves with the partitioned ids so the
// caller can keep the failed ones selected and surface a partial-failure
// banner. `failed` carries the per-id error message for diagnostics.
export interface BulkMoveResult {
  succeeded: string[]
  failed: { id: string; error: string }[]
}

// useBulkMoveGlows fires ONE PATCH /v1/glows/{id} per selected glow (there is
// no bulk endpoint) via Promise.allSettled, so one glow failing never aborts
// the rest. `category: ""` clears the field (move-to-Uncategorized), exactly
// like `title: ""`. Mirrors the apiFetch PATCH shape + auth/qc wiring of
// useUpdateGlow (useGlows.ts) without touching it. onSettled invalidates
// ['glows'] EXACTLY ONCE regardless of N PATCHes — never per-PATCH.
//
// The N-PATCH burst is bounded by the ≤50-glow plan ceiling, so a flat
// allSettled (no concurrency cap) is acceptable for v1.
export function useBulkMoveGlows() {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation<BulkMoveResult, Error, { ids: string[]; category: string }>({
    mutationFn: async ({ ids, category }) => {
      const settled = await Promise.allSettled(
        ids.map(async (id) => {
          await apiFetch<Glow>(`/v1/glows/${id}`, idTokenProvider, {
            method: 'PATCH',
            body: { category },
          })
          return id
        }),
      )
      const succeeded: string[] = []
      const failed: { id: string; error: string }[] = []
      settled.forEach((r, i) => {
        // ids[i] is always defined (settled is index-aligned with ids), but
        // pair the id with its index defensively for the typed access.
        const id = ids[i] ?? ''
        if (r.status === 'fulfilled') succeeded.push(id)
        else failed.push({ id, error: (r.reason as Error)?.message ?? 'failed' })
      })
      return { succeeded, failed }
    },
    // Single invalidation regardless of N PATCHes — never per-PATCH.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['glows'] })
    },
  })
}
